import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";
import moment from "moment-timezone";
import { countries } from "countries-list";

const appAccessOptions = [
  { value: "hms", label: "HMS" },
  { value: "office", label: "WorkWise" },
  { value: "pos", label: "POS" },
  { value: "digital-menu", label: "DIGITAL MENU" },
  { value: "channel-manager", label: "Channel Manager" },
  { value: "booking-engine", label: "Booking Engine" },
  { value: "dynamic-price", label: "Dynamic Price" },
  { value: "feedback", label: "Pulse360" },
];

const DATE_FORMATS = ["MM/dd/yyyy", "dd/MM/yyyy", "yyyy/MM/dd", "yyyy-MM-dd"];

const ORG_TYPES = [
  "Hotels & Resorts",
  "Restaurants & Dining",
  "Travel & Tourism Agencies",
  "Event Planning & Venues",
  "Cruise Lines",
  "Catering Services",
  "Bed & Breakfasts",
  "Vacation Rentals",
  "Casinos & Entertainment",
  "Spas & Wellness Centers",
];

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 2147483647 }),
};

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const isLikelyPhone = (v) => String(v || "").replace(/[^\d+]/g, "").length >= 8;

const formatSampleDate = (format) => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());

  switch (format) {
    case "MM/dd/yyyy":
      return `${mm}/${dd}/${yyyy}`;
    case "dd/MM/yyyy":
      return `${dd}/${mm}/${yyyy}`;
    case "yyyy/MM/dd":
      return `${yyyy}/${mm}/${dd}`;
    case "yyyy-MM-dd":
      return `${yyyy}-${mm}-${dd}`;
    default:
      return `${dd}/${mm}/${yyyy}`;
  }
};

const mapAppAccessToSelect = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  return list
    .map((v) => {
      const opt = appAccessOptions.find((o) => o.value === v);
      return opt || { value: v, label: v };
    })
    .filter(Boolean);
};

const PropertySettingModal = ({ onSubmit, orgData, showModal, onClose }) => {
  // -------------------------
  // Company fields
  // -------------------------
  const [orgName, setOrgName] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [orgPhone, setOrgPhone] = useState("");
  const [currency, setCurrency] = useState("₹");
  const [orgAddress, setOrgAddress] = useState("");
  const [dateFormat, setDateFormat] = useState("dd/MM/yyyy");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [errors, setErrors] = useState({});
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [hsinNo, setHsinNo] = useState("");
  const [gstNo, setGstNo] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("IN");
  const [zipCode, setZipCode] = useState("");
  const [state, setState] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactDesignation, setContactDesignation] = useState("");
  const [orgType, setOrgType] = useState("Hotels & Resorts");

  const [appAccess, setAppAccess] = useState([{ value: "hms", label: "HMS" }]);

  // Optional plan fields
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [planDate, setPlanDate] = useState(() => new Date());

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const timeZoneOptions = useMemo(
    () =>
      moment.tz.names().map((tz) => ({
        value: tz,
        label: `${tz} (UTC${moment.tz(tz).format("Z")})`,
      })),
    []
  );

  const countryOptions = useMemo(
    () =>
      Object.entries(countries)
        .map(([code, c]) => ({ value: code, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  const selectedCountryOption = useMemo(() => {
    return (
      countryOptions.find((o) => o.value === country) ||
      countryOptions.find((o) => o.label === country) ||
      null
    );
  }, [country, countryOptions]);

  const selectedTimeZoneOption = useMemo(() => {
    return timeZoneOptions.find((o) => o.value === timezone) || null;
  }, [timezone, timeZoneOptions]);

  const resetAll = useCallback(() => {
    setOrgName("");
    setOrgEmail("");
    setOrgPhone("");
    setCurrency("₹");
    setOrgAddress("");
    setDateFormat("dd/MM/yyyy");
    setTimezone("Asia/Kolkata");
    setErrors({});
    setHsinNo("");
    setGstNo("");
    setCity("");
    setCountry("IN");
    setZipCode("");
    setState("");
    setContactPerson("");
    setContactDesignation("");
    setOrgType("Hotels & Resorts");
    setAppAccess([{ value: "hms", label: "HMS" }]);
    setBillingCycle("monthly");
    setPlanDate(new Date());
  }, []);

  // Prefill for edit vs create
  useEffect(() => {
    if (!showModal) return;

    if (orgData) {
      setOrgName(orgData.name || orgData.company || "");
      setOrgEmail(orgData.email || "");
      setOrgPhone(orgData.phone || "");
      setCurrency(orgData.company_currency || orgData.currency || "₹");
      setOrgAddress(orgData.company_address || orgData.address || "");
      setDateFormat(orgData.company_date_format || orgData.dateformat || "dd/MM/yyyy");
      setTimezone(orgData.company_timezone || orgData.timezone || "Asia/Kolkata");
      setContactPerson(orgData.contact_person || orgData.contactperson || "");
      setContactDesignation(orgData.contact_designation || orgData.contactdesignation || "");
      setOrgType(orgData.type || orgData.org_type || "Hotels & Resorts");
      setHsinNo(orgData.company_hsin_no || orgData.hsin_no || "");
      setGstNo(orgData.company_gst_no || orgData.gst_no || "");
      setCity(orgData.company_city || orgData.city || "");
      setState(orgData.company_state || orgData.state || "");
      setCountry(orgData.company_country || orgData.country || "IN");
      setZipCode(orgData.company_zip_code || orgData.zip_code || "");
      setAppAccess(mapAppAccessToSelect(orgData.app_access));
      setErrors({});
    } else {
      resetAll();
    }
  }, [orgData, showModal, resetAll]);

  // ---- Google Maps Places Autocomplete (safe load + single instance)
  const loadGoogleMapsScript = useCallback((callback) => {
    const existing = document.getElementById("google-maps-places");
    if (existing && window.google?.maps?.places) {
      callback();
      return;
    }

    if (existing) {
      existing.addEventListener("load", callback);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-places";
    script.async = true;
    script.defer = true;

    // IMPORTANT: move key to env when you can
    script.src =
      "https://maps.googleapis.com/maps/api/js?key=AIzaSyApehT2u7ijeKq0JJpV9A32k_7kPpKaTv0&libraries=places&language=en&region=IN";

    script.onload = callback;
    script.onerror = () => console.error("Failed to load Google Maps API script");
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!showModal) return;

    loadGoogleMapsScript(() => {
      if (!inputRef.current) return;
      if (!window.google?.maps?.places) return;

      // avoid multiple Autocomplete instances
      if (autocompleteRef.current) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["address"],
      });

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (place?.formatted_address) setOrgAddress(place.formatted_address);
      });
    });

    // cleanup when modal closes
    return () => {
      autocompleteRef.current = null;
    };
  }, [showModal, loadGoogleMapsScript]);

  // In edit mode you were doing immediate PUT on timezone/country changes.
  // Kept it, but guarded.
  const handleTimezoneChange = async (opt) => {
    const tz = opt?.value || "";
    setTimezone(tz);

    if (!orgData || !tz) return;
    try {
      await axios.put(
        `${apiUrl}/company/update/${orgData.id || orgData.companyid}`,
        { company_timezone: tz },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Timezone updated successfully!");
    } catch (error) {
      toast.error("Failed to update timezone.");
      console.error(error);
    }
  };

  const handleCountryChange = async (opt) => {
    const val = opt?.value || "";
    setCountry(val);

    if (!orgData || !val) return;
    try {
      await axios.put(
        `${apiUrl}/company/update/${orgData.id || orgData.companyid}`,
        { company_country: val },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Country updated successfully!");
    } catch (error) {
      toast.error("Failed to update country.");
      console.error(error);
    }
  };

  const validateCompanyForm = () => {
    const formErrors = {};

    if (!orgName.trim()) formErrors.orgName = "Property Name is required";

    if (!orgEmail.trim()) formErrors.orgEmail = "Email is required";
    else if (!isValidEmail(orgEmail)) formErrors.orgEmail = "Enter a valid email";

    if (!orgPhone.trim()) formErrors.orgPhone = "Phone is required";
    else if (!isLikelyPhone(orgPhone)) formErrors.orgPhone = "Enter a valid phone number";

    if (!contactPerson.trim()) formErrors.contactPerson = "Contact person is required";
    if (!contactDesignation.trim()) formErrors.contactDesignation = "Contact designation is required";

    if (!city.trim()) formErrors.city = "City is required";
    if (!state.trim()) formErrors.state = "State is required";

    if (!zipCode.trim()) formErrors.zipCode = "ZIP Code is required";

    if (!country) formErrors.country = "Country is required";
    if (!timezone) formErrors.timezone = "Timezone is required";

    if (!appAccess || appAccess.length === 0) formErrors.appAccess = "Select at least one app access";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // -------------------------
  // Submit (Create / Edit)
  // -------------------------
  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    if (!validateCompanyForm()) return;

    const payload = {
      company_name: orgName,
      contactperson: contactPerson,
      contactdesignation: contactDesignation,
      email: orgEmail,
      phone: orgPhone,
      location_address: orgAddress,

      dateformat: dateFormat,
      timezone,

      // backend uses req.body.type -> org_type column
      type: orgType,

      currency,

      // extra info
      hsin_no: hsinNo,
      gst_no: gstNo,
      city,
      state,
      country,
      zip_code: zipCode,

      billingcycle: billingCycle,
      plandate: planDate ? planDate.toISOString().split("T")[0] : null,

      app_access: (appAccess || []).map((o) => o.value),

      // IMPORTANT: This is SUB company creation
      companytype: "sub_company",
    };

    try {
      if (orgData) {
        const response = await axios.put(
          `${apiUrl}/company/update/${orgData.id || orgData.companyid}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        localStorage.setItem("dateFormat", dateFormat);
        toast.success("Property updated successfully!");
        onSubmit?.(response.data);
        onClose?.();
        return;
      }

      // CREATE sub-company (single call; backend links logged-in user)
      const response = await axios.post(`${apiUrl}/company/signup`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const company = response?.data?.company;
      if (!company) {
        toast.error("Created, but response missing company object.");
        return;
      }

      toast.success("Property created successfully!");
      onSubmit?.(response.data);
      resetAll();
      onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to save property.");
      console.error(error);
    }
  };

  useEffect(() => {
    if (!showModal) return undefined;

    document.body.classList.add("ns-psm-open");

    return () => {
      document.body.classList.remove("ns-psm-open");
    };
  }, [showModal]);

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  if (!showModal) return null;

  return (
    <div
      id="property_setting"
      className="ns-psm-root"
      role="dialog"
      aria-modal="true"
    >
      <style>{`
        body.ns-psm-open {
          overflow: hidden !important;
        }

        .ns-psm-root {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          display: flex;
          justify-content: flex-end;
          background: rgba(15, 23, 42, 0.08);
          backdrop-filter: blur(1.5px);
          -webkit-backdrop-filter: blur(1.5px);
        }

        .ns-psm-backdrop {
          position: absolute;
          inset: 0;
          border: 0;
          background: transparent;
          padding: 0;
          cursor: pointer;
        }

        .ns-psm-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: min(880px, 100vw);
          height: 100vh;
          height: 100dvh;
          z-index: 2147483001;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 34%),
            #f8fafc;
          box-shadow: -18px 0 52px rgba(15, 23, 42, 0.16);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .ns-psm-content {
          height: 100%;
          border: 0;
          border-radius: 0;
          background: transparent;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .ns-psm-header {
          flex: 0 0 auto;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.14), transparent 36%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 18px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 14px;
        }

        .ns-psm-title-wrap {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
        }

        .ns-psm-icon {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.22);
          font-weight: 950;
          flex: 0 0 auto;
          font-size: 1.15rem;
        }

        .ns-psm-eyebrow {
          color: #2563eb;
          font-size: 0.68rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-psm-header h4 {
          color: #001f5b;
          font-size: 1.24rem;
          line-height: 1.14;
          font-weight: 950;
          margin: 0;
        }

        .ns-psm-header p {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.42;
          font-weight: 760;
          margin: 5px 0 0;
          max-width: 620px;
        }

        .ns-psm-close {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          border: 1px solid rgba(220, 38, 38, 0.16);
          background: #fff1f2;
          color: #dc2626;
          cursor: pointer;
          font-size: 1.35rem;
          line-height: 1;
          flex: 0 0 auto;
        }

        .ns-psm-form {
          height: 100%;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .ns-psm-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: 15px 18px 112px;
          background: #f8fafc;
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .ns-psm-body .row {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.055);
          padding: 15px 10px;
          margin: 0;
        }

        .ns-psm-body .input-block {
          margin-bottom: 13px !important;
        }

        .ns-psm-body .col-form-label {
          color: #0f172a;
          font-size: 0.77rem;
          font-weight: 950;
          margin: 0 0 7px;
          padding: 0;
        }

        .ns-psm-body .form-control {
          min-height: 44px;
          border-radius: 14px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          color: #0f172a;
          font-size: 0.88rem;
          font-weight: 760;
        }

        .ns-psm-body .form-control:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .ns-psm-body .text-danger {
          font-size: 0.72rem;
          font-weight: 820;
        }

        .ns-psm-footer {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.96);
          border-top: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 -14px 34px rgba(15, 23, 42, 0.08);
          padding: 13px 18px;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          z-index: 4;
        }

        .ns-psm-footer .btn {
          min-height: 42px;
          border-radius: 14px;
          padding: 0 18px;
          font-size: 0.82rem;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .ns-psm-footer .btn-primary {
          min-width: 148px;
          background: linear-gradient(135deg, #16a34a, #22c55e);
          border-color: #16a34a;
          box-shadow: 0 14px 30px rgba(22, 163, 74, 0.20);
        }

        @media (max-width: 760px) {
          .ns-psm-drawer {
            width: 100vw;
          }

          .ns-psm-footer {
            display: grid;
            grid-template-columns: 1fr;
          }

          .ns-psm-footer .btn {
            width: 100%;
          }
        }
      `}</style>

      <button type="button" className="ns-psm-backdrop" aria-label="Close property settings" onClick={handleClose} />
      <div className="ns-psm-drawer">
        <div className="ns-psm-content">
          <div className="ns-psm-header">
            <div className="ns-psm-title-wrap">
              <span className="ns-psm-icon">
                <i className="fa fa-building-o" />
              </span>
              <div>
                <div className="ns-psm-eyebrow">Property Settings</div>
                <h4>{orgData ? "Edit Property" : "Add Property"}</h4>
                <p>
                  {orgData
                    ? "Update identity, compliance fields, app access, and operating defaults."
                    : "Create a new property with app access, billing defaults, and operating settings."}
                </p>
              </div>
            </div>

            <button type="button" className="ns-psm-close" onClick={handleClose} aria-label="Close">
              ×
            </button>
          </div>

          <form onSubmit={handleCompanySubmit} className="ns-psm-form">
            <div className="ns-psm-body">
              <div className="row">
                {/* LEFT */}
                <div className="col-md-6">
                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Property Name <span className="text-danger">*</span>
                    </label>
                    <input className="form-control" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
                    {errors.orgName && <span className="text-danger">{errors.orgName}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Contact Person <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                    />
                    {errors.contactPerson && <span className="text-danger">{errors.contactPerson}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Contact Designation <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      value={contactDesignation}
                      onChange={(e) => setContactDesignation(e.target.value)}
                    />
                    {errors.contactDesignation && (
                      <span className="text-danger">{errors.contactDesignation}</span>
                    )}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">HSIN No</label>
                    <input className="form-control" value={hsinNo} onChange={(e) => setHsinNo(e.target.value)} />
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">GST No</label>
                    <input className="form-control" value={gstNo} onChange={(e) => setGstNo(e.target.value)} />
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">Address</label>
                    <input
                      className="form-control"
                      placeholder="Search for an address"
                      ref={inputRef}
                      value={orgAddress}
                      onChange={(e) => setOrgAddress(e.target.value)}
                    />
                    {!window.google && <small className="text-muted">Loading address search…</small>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      City <span className="text-danger">*</span>
                    </label>
                    <input className="form-control" value={city} onChange={(e) => setCity(e.target.value)} />
                    {errors.city && <span className="text-danger">{errors.city}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Country <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={countryOptions}
                      value={selectedCountryOption}
                      onChange={handleCountryChange}
                      placeholder="Select Country"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={selectPortalStyles}
                    />
                    {errors.country && <span className="text-danger">{errors.country}</span>}
                  </div>
                </div>

                {/* RIGHT */}
                <div className="col-md-6">
                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      type="email"
                      value={orgEmail}
                      onChange={(e) => setOrgEmail(e.target.value)}
                    />
                    {errors.orgEmail && <span className="text-danger">{errors.orgEmail}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Phone <span className="text-danger">*</span>
                    </label>
                    <input className="form-control" value={orgPhone} onChange={(e) => setOrgPhone(e.target.value)} />
                    {errors.orgPhone && <span className="text-danger">{errors.orgPhone}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">Type of Organization</label>
                    <select className="form-control" value={orgType} onChange={(e) => setOrgType(e.target.value)}>
                      {ORG_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">Currency</label>
                    <select className="form-control" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                      <option value="₹">₹</option>
                      <option value="Rs">Rs</option>
                      <option value="$">USD</option>
                      <option value="AUD">AUD</option>
                    </select>
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">Date Format</label>
                    <select className="form-control" value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
                      {DATE_FORMATS.map((f) => (
                        <option key={f} value={f}>
                          {f} - {formatSampleDate(f)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Timezone <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={timeZoneOptions}
                      value={selectedTimeZoneOption}
                      onChange={handleTimezoneChange}
                      placeholder="Select Timezone"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      styles={selectPortalStyles}
                    />
                    {errors.timezone && <span className="text-danger">{errors.timezone}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <input className="form-control" value={state} onChange={(e) => setState(e.target.value)} />
                    {errors.state && <span className="text-danger">{errors.state}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Zip Code <span className="text-danger">*</span>
                    </label>
                    <input className="form-control" value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
                    {errors.zipCode && <span className="text-danger">{errors.zipCode}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      App Access <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={appAccessOptions}
                      value={appAccess}
                      onChange={(vals) => setAppAccess(vals || [])}
                      isMulti
                      placeholder="Select App Access"
                      menuPortalTarget={document.body}
                      styles={selectPortalStyles}
                    />
                    {errors.appAccess && <span className="text-danger">{errors.appAccess}</span>}
                  </div>

                  <div className="input-block mb-3">
                    <label className="col-form-label">Billing Cycle</label>
                    <select
                      className="form-control"
                      value={billingCycle}
                      onChange={(e) => setBillingCycle(e.target.value)}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="ns-psm-footer">
              <button className="btn btn-secondary me-2" type="button" onClick={handleClose}>
                Cancel
              </button>

              <button className="btn btn-primary submit-btn" type="submit">
                {orgData ? "Save Changes" : "Create Property"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PropertySettingModal;